import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Trophy, Users, ListChecks, ClipboardList, Save, Filter,
  ChevronLeft, ChevronRight, Calendar,
} from "lucide-react";

/* ---------- Date helpers (Mon → Sun ISO week) ---------- */
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const snapToMonday = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : new Date();
  const wd = (d.getDay() + 6) % 7; // Mon=0 ... Sun=6
  d.setDate(d.getDate() - wd);
  return toISO(d);
};
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toISO(d);
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const scoreColor = (pct) => {
  if (pct === 0) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  if (pct >= -20) return "bg-amber-100 text-amber-800 border-amber-300";
  if (pct >= -50) return "bg-orange-100 text-orange-800 border-orange-300";
  return "bg-red-100 text-red-800 border-red-300";
};

export default function PerformanceScoring() {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = ["super_admin", "hr", "senior_management", "hod"].includes(role);

  const [weekStart, setWeekStart] = useState(snapToMonday(""));
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const [employees, setEmployees] = useState([]); // {id,name,punch_id,department_name,selected}
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);

  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerSel, setPickerSel] = useState({}); // {id: bool}
  const [savingSel, setSavingSel] = useState(false);
  const [hasSavedSelection, setHasSavedSelection] = useState(false);

  const refresh = async () => {
    setReportLoading(true);
    try {
      const { data } = await api.get(`/performance/weekly?week_start=${weekStart}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load report");
    } finally {
      setReportLoading(false);
    }
  };

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/performance/employees");
      setEmployees(data.rows || []);
      setHasSavedSelection(!!data.has_saved_selection);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) loadEmployees(); }, [isAdmin]);
  useEffect(() => { if (isAdmin) refresh(); /* eslint-disable-next-line */ }, [weekStart]);

  /* Picker control */
  const openPicker = () => {
    const m = {};
    employees.forEach((e) => { m[e.id] = !!e.selected; });
    setPickerSel(m);
    setPickerSearch("");
    setShowPicker(true);
  };
  const filteredEmps = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      (e.name || "").toLowerCase().includes(q)
      || (e.punch_id || "").toLowerCase().includes(q)
      || (e.department_name || "").toLowerCase().includes(q),
    );
  }, [employees, pickerSearch]);
  const pickerCount = Object.values(pickerSel).filter(Boolean).length;
  const allFilteredTicked = filteredEmps.length > 0 && filteredEmps.every((e) => pickerSel[e.id]);
  const toggleAllFiltered = () => {
    const v = !allFilteredTicked;
    const next = { ...pickerSel };
    filteredEmps.forEach((e) => { next[e.id] = v; });
    setPickerSel(next);
  };
  const saveSelection = async () => {
    const ids = Object.entries(pickerSel).filter(([_k, v]) => v).map(([k]) => k);
    if (ids.length === 0) {
      toast.error("Select at least one employee");
      return;
    }
    setSavingSel(true);
    try {
      await api.post("/performance/selection", { employee_ids: ids });
      toast.success(`Saved ${ids.length} employees`);
      setShowPicker(false);
      await loadEmployees();
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingSel(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-sm text-slate-500" data-testid="performance-no-access">
        You don&apos;t have access to this report.
      </div>
    );
  }

  const totals = rows.reduce((a, r) => ({
    d: a.d + r.delegation_task,
    c: a.c + r.checklist_task,
    o: a.o + r.on_time,
    dl: a.dl + r.delayed,
    p: a.p + r.pending,
  }), { d: 0, c: 0, o: 0, dl: 0, p: 0 });

  return (
    <div className="space-y-5" data-testid="performance-scoring-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Trophy className="h-3 w-3" /> Weekly Performance
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Chivo" }}>
            Performance Scoring
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Delegation + Checklist task scoring per employee, week-on-week.
            On-time tasks = no penalty. Each Delayed or Pending = one miss.
          </p>
        </div>
        <Button onClick={openPicker} className="brand-bg brand-bg-hover text-white" data-testid="pick-employees-btn">
          <Users className="h-4 w-4 mr-1.5" />
          {hasSavedSelection
            ? `Edit Selected Employees (${employees.filter((e) => e.selected).length})`
            : "Select Employees"}
        </Button>
      </div>

      {/* Week picker */}
      <Card className="border-slate-200">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
            <Calendar className="h-3 w-3" /> Week
          </div>
          <Button
            size="sm" variant="outline"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            data-testid="week-prev-btn"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(snapToMonday(e.target.value))}
            className="h-8 w-40 text-xs"
            data-testid="week-start-input"
          />
          <div className="text-xs text-slate-600">
            <b className="text-slate-900">{fmtDate(weekStart)}</b>
            {" → "}
            <b className="text-slate-900">{fmtDate(weekEnd)}</b>
            <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded uppercase">Mon → Sun</span>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            data-testid="week-next-btn"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => setWeekStart(snapToMonday(""))}
            data-testid="this-week-btn"
          >
            This Week
          </Button>
          <div className="ml-auto text-xs text-slate-500">
            {reportLoading ? "Loading…" : `${rows.length} employees in report`}
          </div>
        </CardContent>
      </Card>

      {/* Aggregate row */}
      {rows.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><ClipboardList className="h-3 w-3" /> Delegation</div>
            <div className="text-xl font-semibold mt-0.5 tabular-nums">{totals.d}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><ListChecks className="h-3 w-3" /> Checklist</div>
            <div className="text-xl font-semibold mt-0.5 tabular-nums">{totals.c}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase text-emerald-700">On Time</div>
            <div className="text-xl font-semibold mt-0.5 tabular-nums text-emerald-700">{totals.o}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase text-amber-700">Delayed</div>
            <div className="text-xl font-semibold mt-0.5 tabular-nums text-amber-700">{totals.dl}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase text-red-700">Pending</div>
            <div className="text-xl font-semibold mt-0.5 tabular-nums text-red-700">{totals.p}</div>
          </CardContent></Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 text-left">Employee</th>
                  <th className="px-3 py-2 text-right">Delegation</th>
                  <th className="px-3 py-2 text-right">Checklist</th>
                  <th className="px-3 py-2 text-right text-emerald-700">On Time</th>
                  <th className="px-3 py-2 text-right text-amber-700">Delayed</th>
                  <th className="px-3 py-2 text-right text-red-700">Pending</th>
                  <th className="px-3 py-2 text-right">Score %</th>
                </tr>
              </thead>
              <tbody>
                {reportLoading && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
                )}
                {!reportLoading && rows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">
                    {hasSavedSelection
                      ? "No employees in your saved selection. Click 'Edit Selected Employees' above."
                      : "No employees yet. Click 'Select Employees' to begin."}
                  </td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.employee_id} className="border-t border-slate-100" data-testid={`perf-row-${r.employee_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{r.employee_name}</div>
                      <div className="text-[10px] text-slate-400">
                        {r.punch_id || "—"}{r.department_name ? ` · ${r.department_name}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.delegation_task}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.checklist_task}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-medium">{r.on_time}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700 font-medium">{r.delayed}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700 font-medium">{r.pending}</td>
                    <td className="px-3 py-2 text-right">
                      <span
                        className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold tabular-nums ${scoreColor(r.score_pct)}`}
                        data-testid={`score-${r.employee_id}`}
                      >
                        {r.score_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Picker Dialog */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="max-w-2xl bg-white" data-testid="employee-picker-dialog">
          <DialogHeader>
            <DialogTitle>Select Employees for Performance Report</DialogTitle>
            <DialogDescription className="sr-only">
              Tick the employees you want to include in your weekly performance report. Your selection is saved per admin and reused for future weeks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Filter className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
                <Input
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Search name / punch id / dept…"
                  className="h-8 pl-7 text-xs"
                  data-testid="picker-search-input"
                />
              </div>
              <Button size="sm" variant="outline" onClick={toggleAllFiltered} data-testid="toggle-all-btn">
                {allFilteredTicked ? "Untick all visible" : "Tick all visible"}
              </Button>
              <div className="text-xs text-slate-500" data-testid="picker-count">
                <b>{pickerCount}</b> selected · {filteredEmps.length} shown
              </div>
            </div>
            <div className="border border-slate-200 rounded max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody>
                  {filteredEmps.map((e) => (
                    <tr key={e.id} className="border-t first:border-t-0 border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1.5 w-8">
                        <Checkbox
                          checked={!!pickerSel[e.id]}
                          onCheckedChange={(v) => setPickerSel({ ...pickerSel, [e.id]: !!v })}
                          data-testid={`pick-${e.id}`}
                        />
                      </td>
                      <td className="px-1 py-1.5">
                        <div className="font-medium text-slate-900 text-sm">{e.name}</div>
                        <div className="text-[10px] text-slate-400">
                          {e.punch_id || "—"} · {e.department_name} · {e.unit_name}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredEmps.length === 0 && (
                    <tr><td className="px-3 py-6 text-center text-slate-400 text-xs">No matches</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="text-[10px] text-slate-500">
              Your selection is saved against your admin account. It will be remembered next week — add or remove employees anytime.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPicker(false)} disabled={savingSel}>Cancel</Button>
            <Button
              onClick={saveSelection}
              disabled={savingSel || loading}
              className="brand-bg brand-bg-hover text-white"
              data-testid="save-selection-btn"
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              {savingSel ? "Saving…" : `Save (${pickerCount})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
